import { useEffect, useState } from 'react';
import { isNativeShell } from '@/lib/shell';
import { useNamespaceCopy } from '@/i18n/ns';
import billingCopy from '@/i18n/ns/billing';
import { useIntlLocale } from '@/workspace/intl-locale';
import { billingPortalUrl, fetchBilling, type BillingStatus } from './api';
import { ChangePlanDialog } from './change-plan';
import { useBilling } from './context';
import { BillingDraftError } from './draft';
import { PAYMENT_FAILED, PlanSummary } from './plan-summary';
import type { BillingCopy } from './plan-view';

function returnText(state: string | undefined, copy: BillingCopy) {
  if (state === 'pending') return copy.returnPending;
  if (state === 'delayed') return copy.returnDelayed;
  if (state === 'canceled') return copy.returnCanceled;
  return null;
}

/** Settings › Billing: the plan, the balance and the way to payment settings. */
export function CompanyBillingSection({ beforeRedirect }: { beforeRedirect?: () => void }) {
  const copy = useNamespaceCopy(billingCopy);
  const locale = useIntlLocale();
  const [localStatus, setStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const billing = useBilling();
  const shared = billing?.required === true;
  const status = shared ? billing.status : localStatus;
  const loadError = shared && billing.error ? copy.unavailable : null;
  const refresh = async () => {
    setError(null);
    if (shared) return billing.refresh();
    try {
      setStatus(await fetchBilling());
    } catch {
      setError(copy.unavailable);
    }
  };
  useEffect(() => {
    if (shared) return;
    void refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [shared]);
  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (billing?.hasActiveEdit()) throw new Error('billing_edit_active');
      beforeRedirect?.();
      const url = await billingPortalUrl();
      location.assign(url);
    } catch (failure) {
      setBusy(false);
      setError(
        failure instanceof BillingDraftError
          ? failure.reason === 'attachments'
            ? copy.errorAttachments
            : copy.errorStorage
          : failure instanceof Error && failure.message === 'billing_edit_active'
            ? copy.errorEditing
            : copy.portalError,
      );
    }
  };
  const notice = returnText(billing?.returnState, copy);
  const visibleError = error ?? loadError;
  return (
    <>
      <h1>{copy.billingTitle}</h1>
      <p>{copy.billingIntro}</p>
      {notice && (
        <p role="status" aria-live="polite">
          {notice}
        </p>
      )}
      {visibleError && (
        <p role="alert" className="company-error">
          {visibleError}
        </p>
      )}
      {!status && !visibleError && <p role="status">{copy.loading}</p>}
      {!status && visibleError && (
        <button className="company-secondary" onClick={() => void refresh()}>
          {copy.retry}
        </button>
      )}
      {status && (
        <section className="company-empty-state company-billing" data-testid="billing-plan">
          <PlanSummary status={status} copy={copy} locale={locale} />
          {PAYMENT_FAILED.has(status.status) && <p role="alert">{copy.paymentFailed}</p>}
          <p>
            {copy.creditsNeverExpire} <a href="#/settings/company/usage">{copy.seeUsage}</a>
          </p>
          <BillingActions
            status={status}
            copy={copy}
            busy={busy}
            onChangePlan={() => setPlanOpen(true)}
            onPortal={() => void openPortal()}
          />
          <ChangePlanDialog
            open={planOpen}
            onOpenChange={setPlanOpen}
            status={status}
            beforeRedirect={beforeRedirect}
            onChanged={() => void refresh()}
          />
        </section>
      )}
    </>
  );
}

function BillingActions({
  status,
  copy,
  busy,
  onChangePlan,
  onPortal,
}: {
  status: BillingStatus;
  copy: BillingCopy;
  busy: boolean;
  onChangePlan: () => void;
  onPortal: () => void;
}) {
  if (isNativeShell()) return <p>{copy.nativeOnly}</p>;
  if (status.canManage !== true) return <p>{copy.readOnly}</p>;
  return (
    <div className="company-form-actions">
      {status.changePlanAvailable && (
        <button className="company-primary" disabled={busy} onClick={onChangePlan}>
          {status.account.planActive ? copy.changePlan : copy.choosePlan}
        </button>
      )}
      {status.portalAvailable && (
        <button className="company-secondary" disabled={busy} onClick={onPortal}>
          {busy ? copy.opening : copy.portal}
        </button>
      )}
    </div>
  );
}
